import type { Iso2, Iti, ItiUtils } from 'intl-tel-input';

import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/styles';

export const PHONE_INVALID_MESSAGE = 'Enter a valid phone number for the selected country.';

const FALLBACK_COUNTRY: Iso2 = 'us';

// Markets the brief names explicitly, pinned to the top of the country list.
const PRIORITY_COUNTRIES: Iso2[] = ['us', 'gb', 'ru', 'ae'];

let knownCountries: Set<string> | null = null;

function isKnownCountry(iso2: string): iso2 is Iso2 {
  knownCountries ??= new Set(intlTelInput.getAllCountries().map(country => country.iso2));

  return knownCountries.has(iso2);
}

function regionOf(locale: string): string | null {
  try {
    return new Intl.Locale(locale).maximize().region?.toLowerCase() ?? null;
  }
  catch {
    return null;
  }
}

// The number someone is about to type is almost always from where they are, so
// the browser locale picks the starting country. It stays switchable, and no
// geo-IP service is called.
function detectCountry(): Iso2 {
  const locales = navigator.languages.length > 0
    ? navigator.languages
    : [navigator.language];

  for (const locale of locales) {
    const region = regionOf(locale);

    if (region !== null && isKnownCountry(region))
      return region;
  }

  return FALLBACK_COUNTRY;
}

export function createPhoneInput(input: HTMLInputElement): Iti {
  return intlTelInput(input, {
    initialCountry: detectCountry(),
    countryOrder: PRIORITY_COUNTRIES,
    countrySearch: true,
    formatAsYouType: true,
    strictMode: true,
    // ~200 KB of libphonenumber metadata: only fetched once a phone field is
    // actually on screen, and only then do validation and formatting kick in.
    // The cast is needed because the project resolves modules as node10, which
    // ignores the package's `exports` map and leaves the subpath untyped.
    loadUtils: async () => import('intl-tel-input/utils') as Promise<{ default: ItiUtils }>,
  });
}

// E.164 is the storage format. An empty or unparsable field yields ''.
export function phoneE164(iti: Iti): string {
  return iti.getNumber(intlTelInput.NUMBER_FORMAT.E164);
}

export function phoneValidationMessage(iti: Iti, value: string): string {
  if (value.trim() === '')
    return '';

  return iti.isValidNumber() === false ? PHONE_INVALID_MESSAGE : '';
}
